// import action types
import {
  CHANGE_DEPARTMENT_SEGMENT_GROUPS_SEARCH_KEYWORD,
  REQUEST_FETCH_COMPARISON_SURVEY_DATA,
  REQUEST_FETCH_ENGAGEMENT_SURVEY_SEGMENTS,
} from 'actions/actionTypes';
// import actions
import {
  clearComparisonSurveyCondition,
  failureFetchComparisonSurveyData,
  failureFetchEngagementSurveySegments,
  setFilteredDepartmentSegmentGroups,
  successFetchComparisonSurveyData,
  successFetchEngagementSurveySegments,
} from 'actions/backend/engagementSurvey/comparisonActions';
import * as FlashMessageAction from 'actions/flashMessageActions';
// import apis
import {
  requestFetchComparisonQuestionCategory,
  requestFetchComparisonSurveyRatio,
  requestFetchEngagementSurveySegments,
} from 'apis/backend';
import { SegmentGroup } from 'constants/segments';
// import constants
import { QUESTION_CATEGORY_TYPES } from 'constants/surveys';
import { all, call, fork, put, select, take } from 'redux-saga/effects';
// import saga function
import { CallEffectInterface, createLazily } from 'sagas';
// import types
import { RootState } from 'types';
import {
  ComparisonSurveyDataParams,
  ComparisonSurveyDataTypes,
  ComparisonSurveyResultRatioType,
  ComparisonSurveyStatusParams,
  FormatSegmentGroupTypes,
  FormatSegmentsTypes,
  RequestFetchComparisonSurveyData,
  RequestFetchSegments,
  SegmentTypes,
} from 'types/backend/engagementSurvey/comparison';
import { QuestionCategoryTypes } from 'types/backend/engagementSurvey/result';

// requestComparisonSurveyData用params作成
export const createRequestFetchComparisonQuestionCategoryParams = (
  surveyId: string,
  withChildren: boolean,
  categoryType: QuestionCategoryTypes,
  filterSegmentGroup: FormatSegmentGroupTypes[],
): ComparisonSurveyDataParams => {
  const createSegmentGroupTypeIds = (segmentType: SegmentGroup['key']) => {
    const filterSegmentGroupType = filterSegmentGroup.filter(
      (segment) => segment.segmentType === segmentType,
    );
    const segmentGroupTypeIds = !filterSegmentGroupType.length
      ? undefined
      : filterSegmentGroupType.map((segment) => segment.id).join(',');
    return segmentGroupTypeIds;
  };

  return {
    surveyId,
    withChildren,
    categoryType,
    segmentGroupDepartmentIds: createSegmentGroupTypeIds('department'),
    segmentGroupPositionIds: createSegmentGroupTypeIds('position'),
    segmentGroupAgeIds: createSegmentGroupTypeIds('age'),
    segmentGroupEmploymentTypeIds: createSegmentGroupTypeIds('employmentType'),
    segmentGroupRecruitmentTypeIds: createSegmentGroupTypeIds('recruitmentType'),
    segmentGroupGenderIds: createSegmentGroupTypeIds('gender'),
    segmentGroupSeniorityIds: createSegmentGroupTypeIds('seniority'),
  };
};

const createRequestFetchComparisonSurveyRatioParams = (
  surveyId: string,
  filterSegmentGroup: FormatSegmentGroupTypes[],
): ComparisonSurveyDataParams => {
  const createSegmentGroupTypeIds = (segmentType: SegmentGroup['key']) => {
    const filterSegmentGroupType = filterSegmentGroup.filter(
      (segment) => segment.segmentType === segmentType,
    );
    const segmentGroupTypeIds = !filterSegmentGroupType.length
      ? undefined
      : filterSegmentGroupType.map((segment) => segment.id).join(',');
    return segmentGroupTypeIds;
  };

  return {
    surveyId,
    segmentGroupDepartmentIds: createSegmentGroupTypeIds('department'),
    segmentGroupPositionIds: createSegmentGroupTypeIds('position'),
    segmentGroupAgeIds: createSegmentGroupTypeIds('age'),
    segmentGroupEmploymentTypeIds: createSegmentGroupTypeIds('employmentType'),
    segmentGroupRecruitmentTypeIds: createSegmentGroupTypeIds('recruitmentType'),
    segmentGroupGenderIds: createSegmentGroupTypeIds('gender'),
    segmentGroupSeniorityIds: createSegmentGroupTypeIds('seniority'),
  };
};

// セグメント取得
export function* handleRequestFetchSurveys() {
  while (true) {
    const {
      comparisonSurvey,
    }: {
      comparisonSurvey: RequestFetchSegments['comparisonSurvey'];
    } = yield take(REQUEST_FETCH_ENGAGEMENT_SURVEY_SEGMENTS);
    const { rootPath } = yield select((state: RootState) => {
      return state.app;
    });
    const { currentCompany } = yield select((state: RootState) => state.backend.company);
    const {
      payload,
      error,
    }: CallEffectInterface<{
      id: string;
      segments: SegmentTypes[];
    }> = yield call(
      requestFetchEngagementSurveySegments,
      rootPath,
      currentCompany,
      comparisonSurvey.id,
    );
    if (payload && !error) {
      yield put(successFetchEngagementSurveySegments(payload.segments));
    } else {
      yield put(failureFetchEngagementSurveySegments());
    }
  }
}

/**
 * 部署検索条件変更 実行
 */
function* runFilterDepartmentSegmentGroups(departmentSegmentGroupsSearchKeyword: string) {
  yield put(setFilteredDepartmentSegmentGroups(departmentSegmentGroupsSearchKeyword));
}

/**
 * 部署検索条件変更 イベントハンドラ
 */
function* handleFilterDepartmentSegmentGroups() {
  const lazily = createLazily(400);
  while (true) {
    const { departmentSegmentGroupsSearchKeyword } = yield take(
      CHANGE_DEPARTMENT_SEGMENT_GROUPS_SEARCH_KEYWORD,
    );
    yield fork(lazily, runFilterDepartmentSegmentGroups, departmentSegmentGroupsSearchKeyword); // 遅延実行
  }
}

// セレクター
export const appSelector = (state: RootState) => state.app;
export const companySelector = (state: RootState) => state.backend.company;
export const comparisonSelector = (state: RootState) => state.backend.engagementSurvey.comparison;
// 比較データリクエスト
export function* handleRequestFetchComparisonSurveyData() {
  while (true) {
    const { surveyId }: { surveyId: RequestFetchComparisonSurveyData['surveyId'] } = yield take(
      REQUEST_FETCH_COMPARISON_SURVEY_DATA,
    );
    const { rootPath } = yield select(appSelector);
    const { currentCompany } = yield select(companySelector);
    const { formatSegments }: { formatSegments: FormatSegmentsTypes[] } =
      yield select(comparisonSelector);

    // checked value filtering
    const filterSegmentGroup = formatSegments.map((formatSegment) => {
      const filteredCechekedSegmentGroup = formatSegment.surveySegmentGroups.filter(
        (segmentGroup) => {
          return segmentGroup.checked;
        },
      );
      return filteredCechekedSegmentGroup;
    });
    // IEがarray.flat()未対応のためreduceとconcatで代用
    const flatFilterSegmentGroup = filterSegmentGroup.reduce((acc, val) => acc.concat(val), []);
    // 比較対象サーベイデータ取得リクエスト並列で回すために配列作成
    const allRequestFetchComparisonSurveyData = QUESTION_CATEGORY_TYPES.map((categoryType) => {
      const params: ComparisonSurveyDataParams = createRequestFetchComparisonQuestionCategoryParams(
        surveyId,
        true,
        categoryType,
        flatFilterSegmentGroup,
      );
      return call(requestFetchComparisonQuestionCategory, rootPath, currentCompany, params);
    });

    // 比較対象サーベイデータ取得
    const [
      company,
      business,
      organization,
      boss,
      associate,
      treatment,
      recognition,
      opportunity,
    ]: ComparisonSurveyDataTypes[] = yield all(allRequestFetchComparisonSurveyData);
    // error ハンドリング(api側でresolveでerrorオブジェクトをreturnしている)
    const comparisonSurveyDataPayloadError = Object.keys(company).includes('error');

    // エンゲージメントステータス取得
    const surveyStatusParams: ComparisonSurveyStatusParams =
      createRequestFetchComparisonSurveyRatioParams(surveyId, flatFilterSegmentGroup);
    const {
      payload: comparisonSurveyResultRatioPayload,
      error: comparisonSurveyResultRatioError,
    }: CallEffectInterface<ComparisonSurveyResultRatioType> = yield call(
      requestFetchComparisonSurveyRatio,
      rootPath,
      currentCompany,
      surveyStatusParams,
    );

    // テーブル用データ作成
    const comparisonSurveyDataPayload: ComparisonSurveyDataTypes = {
      id: company.id,
      uuid: company.uuid,
      phase: company.phase,
      categories: [
        ...company.categories,
        ...business.categories,
        ...organization.categories,
        ...boss.categories,
        ...associate.categories,
        ...treatment.categories,
        ...recognition.categories,
        ...opportunity.categories,
      ],
    };
    if (
      comparisonSurveyDataPayload &&
      !comparisonSurveyDataPayloadError &&
      comparisonSurveyResultRatioPayload &&
      !comparisonSurveyResultRatioError
    ) {
      const payload = {
        comparisonSurveyDataPayload,
        comparisonSurveyResultRatioPayload,
      };

      yield all([
        put(successFetchComparisonSurveyData(payload)),
        put(clearComparisonSurveyCondition()),
        put(
          FlashMessageAction.addFlashMessage({
            type: 'success',
            message: '対象を追加しました',
          }),
        ),
      ]);
    } else {
      yield put(failureFetchComparisonSurveyData());
    }
  }
}

export default function* comparisonSaga() {
  yield fork(handleRequestFetchSurveys);
  yield fork(handleFilterDepartmentSegmentGroups);
  yield fork(handleRequestFetchComparisonSurveyData);
}
